import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { decompress } from 'lz-string';
import { BehaviorSubject, map, Subject, combineLatest, takeUntil, merge, filter, startWith, take } from 'rxjs';
import { Bizoim, BizoimClientResponse, BizoimClient, BizoimConcentrated, BizoimConcentratedClients } from 'src/app/Interfaces/agent-sales-quantities';
import { LikutWarehousePalletMovementsByDateResponse } from 'src/app/Interfaces/likut';
import { TDashDocSub } from 'src/app/Interfaces/reports';
import { ResponseFromExpress } from 'src/app/Interfaces/SQL-interfaces';
import { ButtonTextPipe } from 'src/app/Pipes/button-text.pipe';
import { CountToTextPipe } from 'src/app/Pipes/count-to-text.pipe';
import { LikutWarehouseService } from 'src/app/services/likut-warehouse.service';
import { StoreService } from 'src/app/services/store.service';
import { ExcelObject, formatAsDateOptions, UtilService } from 'src/app/services/util.service';
import { todayWithZeroTime, translateDateForSQLServer } from 'src/app/Utility/date_functions/SQL_ISO_adapter';
import { utils, writeFile } from 'xlsx';

const date = todayWithZeroTime();

@Component({
	selector: 'app-likut-warehouse-reports-with-datepicker',
	templateUrl: './likut-warehouse-reports-with-datepicker.component.html',
	styleUrls: ['./likut-warehouse-reports-with-datepicker.component.scss'],
})
export class LikutWarehouseReportsWithDatepickerComponent implements OnInit, OnDestroy {
	_likutWarehouse = inject(LikutWarehouseService);

	private _ss = inject(StoreService);
	private router = inject(Router);
	private fb = inject(FormBuilder);
	private _util = inject(UtilService);

	constructor() {}

	dates = {
		min: (() => {
			const d = new Date();
			d.setFullYear(d.getFullYear() - 3);
			return d;
		})(),
		max: new Date(),
	};

	form = this.fb.group({
		Search: [''],
		from: [new Date(date), Validators.required],
		to: [new Date(date), Validators.required],
		warehouses: [<string[]>[]],
		users: [<string[]>[]],
	});

	mustPickDatePopup = true;
	hasPickedAtLeastOnce = false;

	formValues = this.form.valueChanges.pipe(startWith(this.form.value));

	allPallets$ = new BehaviorSubject<LikutWarehousePalletMovementsByDateResponse[]>([]);
	allPalletsDisplay$ = combineLatest([this.allPallets$, this.formValues]).pipe(
		map(([allPallets, { Search, warehouses, users }]) => {
			return this.filterPallets(allPallets, { Search, warehouses, users });
		}),
	);
	warehouses$ = this.allPallets$.pipe(
		filter(inventory => inventory.length > 0),
		map(inventory => Array.from(new Set(inventory.map(i => i.WarehouseID)))),
	);

	users$ = this.allPallets$.pipe(
		filter(inventory => inventory.length > 0),
		map(inventory => Array.from(new Set(inventory.map(i => i.UserID)))),
	);

	totalAllPallets$ = this.allPalletsDisplay$.pipe(map(movements => movements.length));

	destroy$ = new Subject<void>();

	bizoimTotalQuantity$ = new BehaviorSubject<number>(0);
	bizoimTotalSum$ = new BehaviorSubject<number>(0);
	bizoimAverageSum$ = new BehaviorSubject<number>(0);

	loadingMainQuery = false;

	windowWidth: number;

	isAdmin = location.pathname.includes('admin');

	ngOnInit(): void {
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			if (localStorage.getItem('CustomerInformation') !== null) {
				this._ss.CustomerInformation = decompress(localStorage.getItem('CustomerInformation'));
				this._ss.CustomerInformation = JSON.parse(this._ss.CustomerInformation);
			}
		}
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		if (!this._likutWarehouse.warehousesAll$.value.length) this._likutWarehouse.updateWarehousesAll();

		this.windowWidth = window.innerWidth;

		window.scrollTo(0, 0);

		if (!localStorage.getItem('UserName') && !localStorage.getItem('AgentId')) {
			this.router.navigate(['login']);
		}

		this._ss.UserNameConnected = localStorage.getItem('UserName');
		this._ss.AgentIdConnected = localStorage.getItem('AgentId');
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	handleClosePopup() {
		this.mustPickDatePopup = false;
		if (!this.hasPickedAtLeastOnce) {
			this.handleNavigation();
			return;
		}
	}

	handleFormSubmit() {
		this.loadingMainQuery = true;

		let { from, to } = this.form.value;

		let toAdd = [];

		if (!this.isAdmin) {
			toAdd.push(this._ss.AgentIdConnected);
		}

		// if (this.isAdmin && Agent && Agent.length) {
		// 	toAdd = [Agent, true];
		// }

		from = new Date(Intl.DateTimeFormat('en-il', {}).format(new Date(+from)).split('/').reverse().join('/'));
		to = new Date(Intl.DateTimeFormat('en-il', {}).format(new Date(+to)).split('/').reverse().join('/'));

		from = translateDateForSQLServer(from);
		to = translateDateForSQLServer(to);

		// if (from.getTime() === to.getTime()) {
		to.setHours(24 - new Date().getTimezoneOffset() / 60);
		// }

		this._likutWarehouse.getPalletMovementReportByDateRange(from, to).subscribe({
			next: res => {
				if (res === 'ConnectionError' || res === 'RequestError') {
					this.handleError();
					return;
				}

				this.allPallets$.next(res.recordset);

				this.mustPickDatePopup = false;
				this.hasPickedAtLeastOnce = true;

				this.loadingMainQuery = false;

				this.form.controls.Search.setValue(this.form.controls.Search.value);
			},
			error: err => {
				this.handleError();
			},
		});
	}

	handleError() {
		alert('ישנה תקלת תקשורת, אנא נסה שנית מאוחר יותר');
		this.handleNavigation();
		return;
	}

	changeDateValue(val: 'month' | 'year') {
		const { from, to } = this.form.value;

		if (val === 'month') {
			from.setMonth(from.getMonth() - 1);
		} else {
			from.setFullYear(from.getFullYear() - 1);
		}

		this.form.patchValue({ from, to });
	}

	SearchClear() {
		if (this.form.controls['Search'].value) {
			this.form.controls['Search'].reset();
		}

		this.clearEverything();
	}

	//TODO refactor into two different methods

	clearEverything() {
		// this.Bizoim$.next(this.Bizoim);
		// this.refreshSubjects('Bizoim');
	}

	handleNavigation() {
		history.back();
	}

	sortArr = [false, false, false, false];

	sort(valueToSortBy: string, arrayToSort: string) {
		const opt: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' };

		let bool: boolean, i: number;

		let arrOverride: Bizoim[] | BizoimClient[];

		switch (valueToSortBy) {
			case 'ProductId':
			case 'ClientId':
				i = 0;
				break;
			case 'Product_Name':
			case 'Client_Name':
				i = 1;
				break;
			case 'totqty':
				i = 2;
				break;
			case 'totsum':
				i = 3;
				break;

			default:
				console.log('I should never return');
				return;
		}
		bool = this.sortArr[i];
		this.sortArr = this.sortArr.map(_ => false);

		this.sortArr[0] = bool;

		const finalArr = arrOverride || this[arrayToSort];

		if (bool) {
			finalArr.sort((a, b) => new Intl.Collator('he', opt).compare(b[String(valueToSortBy)], a[String(valueToSortBy)]));
		} else {
			finalArr.sort((a, b) => new Intl.Collator('he', opt).compare(a[String(valueToSortBy)], b[String(valueToSortBy)]));
		}

		this.sortArr[i] = !bool;
	}

	downloadExcel() {
		const dataObject: ExcelObject = { data: [], filename: '', extension: 'xlsx' };

		const getFormatOptions = (str: 'date' | 'hour'): formatAsDateOptions => ({ isTrueISO: true, onlyTime: str === 'hour' });

		const longestStringInColumn: any = {};

		const countPipe = CountToTextPipe.prototype.transform;

		let values = this.allPallets$.value;

		const { warehouses, users, Search } = this.form.value;

		values = this.filterPallets(values, { warehouses, users, Search });

		dataObject.data = values.map(b => {
			const obj: { [key: string]: string | number } = {
				'מזהה תנועה': b.MoveID,
				'סוג תנועה': countPipe(b.Count),
				תאריך: this._util.formatAsDate(b.MoveDate, getFormatOptions('date')),
				שעה: this._util.formatAsDate(b.MoveDate, getFormatOptions('hour')),
				מחסן: b.WarehouseID,
				משתמש: b.UserID,
				משטח: b.PaletteID,
				'מק"ט': b.ProductId,
				פריט: b.ProductName,
			};

			for (const [key, value] of Object.entries(obj)) {
				longestStringInColumn[key] ??= 0;
				longestStringInColumn[key] = Math.max(String(key).length, String(value).length, longestStringInColumn[key], 3);
			}
			return obj;
		});

		this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(buttonsAndText => {
			dataObject.filename = ButtonTextPipe.prototype.transform(buttonsAndText['date-range-pallet-movement-report']);
		});

		const ws = utils.json_to_sheet(dataObject.data);

		ws['!cols'] = Object.values(longestStringInColumn).map((columnWidth: number) => ({ wch: columnWidth }));

		const wb = utils.book_new();
		if (!wb.Workbook) wb.Workbook = {};
		if (!wb.Workbook.Names) wb.Workbook.Views = [{}];
		wb.Workbook.Views[0].RTL = true;
		utils.book_append_sheet(wb, ws, dataObject.filename);
		writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, { bookType: dataObject.extension, sheet: dataObject.filename, compression: true, cellStyles: true });
	}

	filterPallets(
		pallets: LikutWarehousePalletMovementsByDateResponse[],
		{ warehouses, users, Search }: { warehouses: string[]; users: string[]; Search: string },
	): LikutWarehousePalletMovementsByDateResponse[] {
		let filtered = pallets;

		if (Search) {
			Search = Search.toLowerCase().trim();
			filtered = filtered.filter(p => p.ProductId.toLowerCase().includes(Search) || p.ProductName.toLowerCase().includes(Search));
		}
		if (warehouses.length) {
			filtered = filtered.filter(p => warehouses.includes(p.WarehouseID));
		}

		if (users.length) {
			filtered = filtered.filter(p => users.includes(p.UserID));
		}

		return filtered;
	}
}
